import { ClassicEditor as CkEditor5ClassicEditor } from '@ckeditor/ckeditor5-editor-classic';
import type { EditorConfig } from 'ckeditor5/src/core';
import { Highlight } from '@ckeditor/ckeditor5-highlight';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { Heading } from '@ckeditor/ckeditor5-heading';
import {
	Bold,
	Italic,
	Strikethrough,
	Subscript,
	Superscript,
	Underline
} from '@ckeditor/ckeditor5-basic-styles';
import { List } from '@ckeditor/ckeditor5-list';
import {
	SpecialCharacters,
	SpecialCharactersEssentials
} from '@ckeditor/ckeditor5-special-characters';
import { AutoLink, Link, LinkImage } from '@ckeditor/ckeditor5-link';
import {
	Image,
	ImageCaption,
	ImageInsert,
	ImageResize,
	ImageStyle,
	ImageTextAlternative,
	ImageToolbar
} from '@ckeditor/ckeditor5-image';
import { Font } from '@ckeditor/ckeditor5-font';
import { Alignment } from '@ckeditor/ckeditor5-alignment';
import { SourceEditing } from '@ckeditor/ckeditor5-source-editing';
import { GeneralHtmlSupport } from '@ckeditor/ckeditor5-html-support';
import {
	Table,
	TableToolbar,
	TableColumnResize,
	TableUtils,
	TableProperties,
	TableCellProperties
} from '@ckeditor/ckeditor5-table';
import { HorizontalLine } from '@ckeditor/ckeditor5-horizontal-line';
import { MediaEmbed } from '@ckeditor/ckeditor5-media-embed';
import { Paragraph } from 'ckeditor5/src/paragraph';
import { Clipboard } from 'ckeditor5/src/clipboard';

import {
	MgnlImageInsert,
	MgnlLink,
	MgnlNote,
	SmartButton,
	DAMPlugin,
	CleanPaste
} from '@magnolia/ckeditor5-plugins';

import './mgnl-css-framework-compatible.css';

const BUILTIN_PLUGINS = [
	Essentials,
	Heading,
	Paragraph,
	Bold,
	Italic,
	Underline,
	Strikethrough,
	Subscript,
	Superscript,
	List,
	SpecialCharacters,
	SpecialCharactersEssentials,
	GeneralHtmlSupport,
	AutoLink,
	Link,
	MgnlLink,
	Image,
	ImageToolbar,
	ImageCaption,
	ImageStyle,
	ImageResize,
	LinkImage,
	ImageInsert,
	ImageTextAlternative,
	MgnlImageInsert,
	MgnlNote,
	DAMPlugin,
	SmartButton,
	Font,
	Alignment,
	SourceEditing,
	Table,
	TableToolbar,
	TableColumnResize,
	TableUtils,
	TableProperties,
	TableCellProperties,
	Highlight,
	HorizontalLine,
	MediaEmbed,
	Clipboard,
	CleanPaste
];

const DEFAULT_CONFIG: EditorConfig = {
	toolbar: [
		'heading',
		'paragraph',
		'|',
		'bold',
		'italic',
		'underline',
		'strikethrough',
		'subscript',
		'superscript',
		'specialCharacters',
		'|',
		'fontSize',
		'fontColor',
		'fontBackgroundColor',
		'|',
		'numberedList',
		'bulletedList',
		'alignment',
		'insertTable',
		'horizontalLine',
		'|',
		'link',
		'mgnlAssetsLink',
		'mgnlPagesLink',
		'insertImage',
		'damBrowser',
		'mediaEmbed',
		'|',
		'mgnlNote',
		'smartButton',
		'|',
		'sourceEditing',
		'|',
		'undo',
		'redo'
	],

	cleanPaste: {
		removeColors: true,
		removeBackgroundColors: true,
		removeFontFamilies: true,
		removeLineHeights: true,
		removeMargins: false, // Keep margins for structure
		removePadding: false, // Keep padding for structure
		preserveListStyles: true,
		preserveTableStyles: true
	},

	fontSize: {
		options: [ 9, 11, 13, 'default', 17, 19, 21 ]
	},
	fontColor: {
		colors: [
			{
				color: 'hsl(0, 0%, 0%)',
				label: 'Black'
			},
			{
				color: 'hsl(0, 0%, 30%)',
				label: 'Dim grey'
			},
			{
				color: 'hsl(0, 0%, 60%)',
				label: 'Grey'
			},
			{
				color: 'hsl(0, 0%, 100%)',
				label: 'White',
				hasBorder: true
			},
			{
				color: 'hsl(0, 75%, 60%)',
				label: 'Red'
			},
			{
				color: 'hsl(30, 75%, 60%)',
				label: 'Orange'
			},
			{
				color: 'hsl(60, 75%, 60%)',
				label: 'Yellow'
			},
			{
				color: 'hsl(90, 75%, 60%)',
				label: 'Light green'
			},
			{
				color: 'hsl(120, 75%, 60%)',
				label: 'Green'
			},
			{
				color: 'hsl(150, 75%, 60%)',
				label: 'Aquamarine'
			},
			{
				color: 'hsl(180, 75%, 60%)',
				label: 'Turquoise'
			},
			{
				color: 'hsl(210, 75%, 60%)',
				label: 'Light blue'
			},
			{
				color: 'hsl(240, 75%, 60%)',
				label: 'Blue'
			},
			{
				color: 'hsl(270, 75%, 60%)',
				label: 'Purple'
			}
		]
	},
	fontBackgroundColor: {
		colors: [
			{
				color: 'hsl(0, 0%, 0%)',
				label: 'Black'
			},
			{
				color: 'hsl(0, 0%, 30%)',
				label: 'Dim grey'
			},
			{
				color: 'hsl(0, 0%, 60%)',
				label: 'Grey'
			},
			{
				color: 'hsl(0, 0%, 100%)',
				label: 'White',
				hasBorder: true
			},
			{
				color: 'hsl(0, 75%, 60%)',
				label: 'Red'
			},
			{
				color: 'hsl(30, 75%, 60%)',
				label: 'Orange'
			},
			{
				color: 'hsl(60, 75%, 60%)',
				label: 'Yellow'
			},
			{
				color: 'hsl(90, 75%, 60%)',
				label: 'Light green'
			},
			{
				color: 'hsl(120, 75%, 60%)',
				label: 'Green'
			},
			{
				color: 'hsl(150, 75%, 60%)',
				label: 'Aquamarine'
			},
			{
				color: 'hsl(180, 75%, 60%)',
				label: 'Turquoise'
			},
			{
				color: 'hsl(210, 75%, 60%)',
				label: 'Light blue'
			},
			{
				color: 'hsl(240, 75%, 60%)',
				label: 'Blue'
			},
			{
				color: 'hsl(270, 75%, 60%)',
				label: 'Purple'
			}
		]
	},
	table: {
		contentToolbar: [
			'tableColumn',
			'tableRow',
			'mergeTableCells',
			'tableCellProperties',
			'tableProperties'
		]
	},
	image: {
		toolbar: [
			'imageStyle:inline',
			'imageStyle:block',
			'imageStyle:side',
			'|',
			'toggleImageCaption',
			'imageTextAlternative',
			'|',
			'resizeImage'
		],
		insert: {
			type: 'auto',
			integrations: [ 'mgnlurl' ]
		}
	},
	heading: {
		options: [
			{
				model: 'paragraph',
				title: 'Paragraph',
				class: 'ck-heading_paragraph'
			},
			{
				model: 'heading1',
				view: 'h1',
				title: 'Heading 1',
				class: 'ck-heading_heading1'
			},
			{
				model: 'heading2',
				view: 'h2',
				title: 'Heading 2',
				class: 'ck-heading_heading2'
			},
			{
				model: 'heading3',
				view: 'h3',
				title: 'Heading 3',
				class: 'ck-heading_heading3'
			},
			{
				model: 'heading4',
				view: 'h4',
				title: 'Heading 4',
				class: 'ck-heading_heading4'
			},
			{
				model: 'heading5',
				view: 'h5',
				title: 'Heading 5',
				class: 'ck-heading_heading5'
			},
			{
				model: 'heading6',
				view: 'h6',
				title: 'Heading 6',
				class: 'ck-heading_heading6'
			}
		]
	},
	mediaEmbed: {
		previewsInData: true,
		extraProviders: [
			{
				name: 'valeoservice-mediacenter',
				url: /^(https?:\/\/)?admin-mediacenter\.valeoservice\.com\/.+/i,
				html: ( match: RegExpMatchArray ): string => {
					const url = match[ 0 ].startsWith( 'http' ) ? match[ 0 ] : `https://${ match[ 0 ] }`;
					return (
						'<div style="position: relative; padding-bottom: 56.25%; height: 0;">' +
						`<iframe src="${ url }"` +
						' style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;"' +
						' frameborder="0" allow="autoplay; encrypted-media" allowfullscreen></iframe>' +
						'</div>'
					);
				}
			},
			{
				name: 'keepeek-assets',
				url: /^(https?:\/\/)?assets-valeo\.keepeek\.com\/.+\.mp4$/i,
				html: ( match: RegExpMatchArray ): string => {
					const url = match[ 0 ].startsWith( 'http' ) ? match[ 0 ] : `https://${ match[ 0 ] }`;
					return (
						'<div style="position: relative; padding-bottom: 56.25%; height: 0;">' +
						'<video controls style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;">' +
						`<source src="${ url }" type="video/mp4">Your browser does not support the video tag.</video>` +
						'</div>'
					);
				}
			}
		]
	},
	htmlSupport: {
		allow: [
			{
				name: 'a',
				attributes: {
					href: true,
					target: true,
					rel: true,
					class: true,
					id: true
				},
				classes: [ 'ck-link-element', 'smart-button', 'smart-button-centered', 'smart-button-right' ],
				styles: {
					'text-decoration': true,
					'text-decoration-color': true,
					'color': true
				}
			},
			{
				name: 'iframe',
				attributes: {
					src: true,
					frameborder: true,
					allowfullscreen: true,
					allow: true,
					width: true,
					height: true,
					style: true,
					class: true
				},
				styles: {
					'border': true,
					'width': true,
					'height': true,
					'display': true,
					'margin': true,
					'padding': true,
					'border-radius': true,
					'max-width': true,
					'box-shadow': true,
					'position': true
				}
			},
			{
				name: /^(p|div|span|h[1-6]|strong|em|u|s|sub|sup|br|li|ul|ol)$/,
				attributes: {
					class: true,
					id: true
				},
				styles: {
					'font-size': /^(9px|11px|13px|14px|17px|19px|21px)$/,
					'color': /^hsl\(\d+,\s*\d+%,\s*\d+%\)$/,
					'background-color': /^hsl\(\d+,\s*\d+%,\s*\d+%\)$/,
					'text-align': /^(left|center|right|justify)$/
				}
			}
		],
		allowEmpty: [ 'a', 'iframe' ]
	}
};

class ClassicEditor extends CkEditor5ClassicEditor {
	public static override builtinPlugins = BUILTIN_PLUGINS;
	public static override defaultConfig = DEFAULT_CONFIG;
}

export default { ClassicEditor };
